import {
  keepPreviousData,
  useMutation,
  useQuery,
} from '@tanstack/solid-query';
import { queryClient } from '@queries/client';
import { whatsappGraphql } from './client';
import { whatsappKeys } from './keys';

export interface WhatsappMessage {
  chatJid: string;
  senderJid: string;
  fromMe: boolean;
  wamid: string;
  body: string;
  msgType: string;
  /** Author's WhatsApp push name; empty if unknown. */
  senderName: string;
  tsUnixMs: string;
}

export interface WhatsappSendResult {
  messageId: string;
  state: string;
}

const MESSAGES_QUERY = `query WhatsappMessages($accountId: String!, $chatJid: String!, $limit: Int) {
  user {
    whatsappMessages(accountId: $accountId, chatJid: $chatJid, limit: $limit) {
      chatJid
      senderJid
      fromMe
      wamid
      body
      msgType
      senderName
      tsUnixMs
    }
  }
}`;

export async function fetchWhatsappMessages(
  accountId: string,
  chatJid: string,
  limit = 100
): Promise<WhatsappMessage[]> {
  const data = await whatsappGraphql<{
    user: { whatsappMessages: WhatsappMessage[] };
  }>(MESSAGES_QUERY, { accountId, chatJid, limit });
  return data.user.whatsappMessages;
}

/** Messages in a conversation (oldest-first), polled for live inbound. */
export function useWhatsappMessagesQuery(
  accountId: () => string | undefined,
  chatJid: () => string | undefined,
  refetchInterval = 30000
) {
  return useQuery(() => {
    const account = accountId();
    const chat = chatJid();
    return {
      queryKey: whatsappKeys.messages(account ?? '', chat ?? ''),
      queryFn: () => fetchWhatsappMessages(account as string, chat as string),
      enabled: !!account && !!chat,
      // Keep the previously-open thread visible while switching chats so the
      // message pane doesn't blank (and suspend the panel) on every change.
      placeholderData: keepPreviousData,
      // Ride out transient gateway blips, then slow-poll (not stop) on a
      // settled error so the thread recovers on its own.
      retry: 2,
      refetchInterval: (query) =>
        query.state.status === 'error' ? 30000 : refetchInterval,
    };
  });
}

const SEND_TEXT = `mutation WhatsappSendText($accountId: String!, $to: String!, $text: String!, $isGroup: Boolean) {
  whatsappSendText(accountId: $accountId, to: $to, text: $text, isGroup: $isGroup) {
    messageId
    state
  }
}`;

export function useSendTextMutation() {
  return useMutation(() => ({
    gcTime: 0,
    mutationFn: async (vars: {
      accountId: string;
      to: string;
      text: string;
      isGroup?: boolean;
    }) => {
      const data = await whatsappGraphql<{
        whatsappSendText: WhatsappSendResult;
      }>(SEND_TEXT, vars);
      return data.whatsappSendText;
    },
    onSettled: (_data, _err, vars) => {
      // The gateway persists outbound sends, so refetch the thread + chat list
      // to surface the message we just sent.
      queryClient.invalidateQueries({
        queryKey: whatsappKeys.messages(vars.accountId, vars.to),
      });
      queryClient.invalidateQueries({
        queryKey: whatsappKeys.chats(vars.accountId),
      });
    },
  }));
}
